<template>
  <div class="flex min-h-screen flex-col justify-center bg-gray-50 py-12 sm:px-6 lg:px-8">
    <div class="sm:mx-auto sm:w-full sm:max-w-md">
      <img
        class="mx-auto h-12 w-auto"
        src="../../assets/imgs/logo/logo-multiple-no-padding.svg"
        alt="Workflow"
      />
    </div>

    <div class="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
      <div class="bg-white px-4 py-8 shadow sm:rounded-lg sm:px-10">
        <form @submit.prevent="login" class="space-y-6" action="#" method="POST">
          <div>
            <label for="email" class="block text-sm font-medium text-gray-700">
              {{ $t("authentication.login.email") }}
            </label>
            <div class="mt-1">
              <input
                id="email"
                name="email"
                type="email"
                autocomplete="email"
                required=""
                v-model="email"
                class="focus:outline-none block w-full appearance-none rounded-md border border-gray-300 px-3 py-2 placeholder-gray-400 shadow-sm focus:border-oaiGray-100 focus:ring-oaiGray-100 sm:text-sm"
              />
            </div>
          </div>

          <div>
            <label for="password" class="block text-sm font-medium text-gray-700">
              {{ $t("authentication.login.password") }}
            </label>
            <div class="mt-1 relative">
              <input
                id="password"
                name="password"
                :type="showPassword ? 'text' : 'password'"
                autocomplete="current-password"
                required=""
                v-model="password"
                class="focus:outline-none block w-full appearance-none rounded-md border border-gray-300 pl-3 pr-9 py-2 placeholder-gray-400 shadow-sm focus:border-oaiGray-100 focus:ring-oaiGray-100 sm:text-sm"
              />
              <EyeIcon
                class="w-5 h-5 absolute top-2 right-3 text-gray-600 cursor-pointer"
                @click="showPassword = true"
                v-if="!showPassword"
              />
              <EyeSlashIcon
                class="w-5 h-5 absolute top-2 right-3 text-gray-600 cursor-pointer"
                @click="showPassword = false"
                v-if="showPassword"
              />
            </div>
          </div>

          <div class="rounded-md bg-red-50 p-4" v-if="passwordAlert">
            <div class="flex">
              <div class="shrink-0">
                <XCircleIcon class="h-5 w-5 text-red-400" aria-hidden="true" />
              </div>
              <div class="ml-3">
                <h3 class="text-sm font-medium text-red-800">
                  {{ $t("authentication.login.wrong_email_or_password") }}
                </h3>
              </div>
            </div>
          </div>
          <div class="text-sm text-yellow-900 text-right">
            <router-link to="/forgot-password" class="font-sm hover:text-yellow-700">
              {{ $t("authentication.login.forgot_password") }}
            </router-link>
          </div>
          <div>
            <button
              type="submit"
              :disabled="loginLoading"
              class="focus:outline-none flex w-full justify-center rounded-md border border-transparent bg-yellow-500 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-yellow-600 focus:ring-2 focus:ring-yellow-500 focus:ring-offset-2"
            >
              <div v-if="loginLoading">
                <svg
                  class="h-5 w-5 animate-spin text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    class="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    stroke-width="4"
                  ></circle>
                  <path
                    class="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
              </div>
              <div v-else>{{ $t("authentication.login.sign_in") }}</div>
            </button>
          </div>
          <div class="flex items-center gap-3 text-sm text-gray-400">
            <div class="border-t flex-1" />
            {{ $t("authentication.login.or") }}
            <div class="border-t flex-1" />
          </div>
          <div class="rounded-md bg-red-50 p-4" v-if="socialPasswordAlert">
            <div class="flex">
              <div class="shrink-0">
                <XCircleIcon class="h-5 w-5 text-red-400" aria-hidden="true" />
              </div>
              <div class="ml-3">
                <h3 class="text-sm font-medium text-red-800">
                  {{ $t("authentication.login.social_error") }}
                </h3>
              </div>
            </div>
          </div>
          <button
            class="border px-5 py-2 rounded-lg flex gap-3 w-full justify-center"
            type="button"
            style="font-family: Montserrat-SemiBold, system-ui"
            @click="triggerMicrosoftLogin"
          >
            <img
              alt="microsoft logo"
              src="data:image/svg+xml,%3csvg%20width='21'%20height='21'%20viewBox='0%200%2021%2021'%20fill='none'%20xmlns='http://www.w3.org/2000/svg'%3e%3crect%20x='11.375'%20y='11.375'%20width='8.75'%20height='8.75'%20fill='%23FEBA08'/%3e%3crect%20x='0.875'%20y='11.375'%20width='8.75'%20height='8.75'%20fill='%2305A6F0'/%3e%3crect%20x='11.375'%20y='0.875'%20width='8.75'%20height='8.75'%20fill='%2380BC06'/%3e%3crect%20x='0.875'%20y='0.875'%20width='8.75'%20height='8.75'%20fill='%23F25325'/%3e%3c/svg%3e"
            /><span>{{ $t("authentication.login.sign_in_with_microsoft") }}</span>
          </button>
        </form>
      </div>
    </div>
    <Footer></Footer>
  </div>
</template>

<script>
import { XCircleIcon, EyeIcon, EyeSlashIcon } from "@heroicons/vue/24/solid";
import { segmentAnalytics } from "@/assets/scripts";
import AuthMixins from "@/mixins/AuthMixins";
import logger from "@/services/logger";
import Footer from "@/views/auth/components/Footer";

export default {
  name: "Login",
  components: {
    XCircleIcon,
    Footer,
    EyeIcon,
    EyeSlashIcon,
  },
  mixins: [AuthMixins],
  methods: {
    async login() {
      this.loginLoading = true;
      this.passwordAlert = false;

      try {
        await this.signIn(this.email, this.password);

        const returnUrl = this.getReturnUrl();

        this.$router.push(returnUrl);
      } catch (error) {
        const is401 = error?.response?.status === 401;
        const is403 = error?.response?.status === 403;
        if (
          error.message !== "Incorrect username or password." &&
          error.message !== "Network error" &&
          !is403 &&
          !is401
        ) {
          logger.error("Unable to sign in", error);
        }
        this.passwordAlert = true;
        this.loginLoading = false;
      }
    },
    async triggerMicrosoftLogin() {
      this.loginLoading = true;
      this.passwordAlert = false;
      this.socialPasswordAlert = false;

      segmentAnalytics("microsoft_login");
      try {
        await this.microsoftSignIn();
        segmentAnalytics("microsoft_login_success");
        this.$router.push(this.getReturnUrl());
      } catch (error) {
        if (
          error !== "No current user" &&
          error?.message !== "user_cancelled: User cancelled the flow." &&
          !error?.message.includes("interaction_in_progress")
        ) {
          logger.error(error);
        }
        this.socialPasswordAlert = true;
      } finally {
        this.loginLoading = false;
      }
    },
  },
  data() {
    return {
      email: "",
      password: "",
      passwordAlert: false,
      socialPasswordAlert: false,
      loginLoading: false,
      showPassword: false,
    };
  },
};
</script>
